import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import type { GeoJSON as LeafletGeoJSON, PathOptions } from "leaflet";
import "leaflet/dist/leaflet.css";
import type { GeoJSONFeatureCollection } from "@/types";
import envConfig from "@/config/env";

/** CARTO raster Positron tiles. Without a key CARTO watermarks every tile. */
const CARTO_BASEMAP_KEY = envConfig.cartoBasemapKey;
const CARTO_LIGHT_ALL_URL = CARTO_BASEMAP_KEY
  ? `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_BASEMAP_KEY}`
  : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

// Fix default icon paths for Leaflet in Vite
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

interface MapProps {
  center?: [number, number];
  zoom?: number;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  showZoomControls?: boolean;
  doubleClickZoom?: boolean;
  closePopupOnClick?: boolean;
}

// Calls map.invalidateSize() whenever the map container is resized.
// Required when the map lives inside a resizable panel (e.g. mosaic tile).
function MapResizeInvalidator() {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const observer = new ResizeObserver(() => {
      map.invalidateSize({ animate: false });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);
  return null;
}

// Component to fit bounds when GeoJSON changes
// Uses a stable key to only fit bounds when geographic data actually changes
function FitBounds({
  geoJSON,
  dataKey,
}: {
  geoJSON: GeoJSONFeatureCollection | null;
  dataKey: string;
}) {
  const map = useMap();

  useEffect(() => {
    if (geoJSON && geoJSON.features.length > 0) {
      const geoJsonLayer = L.geoJSON(geoJSON as any);
      const bounds = geoJsonLayer.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [dataKey, map]); // Only run when dataKey changes (i.e., when geographic data changes)

  return null;
}

/**
 * Fits the map to the hurricane track path.
 * Use when track coordinates are available to zoom to the path instead of impact zones.
 */
export function FitBoundsToTrack({
  trackCoordinates,
  dataKey,
  padding = 80,
}: {
  trackCoordinates: [number, number][];
  dataKey: string;
  padding?: number;
}) {
  const map = useMap();

  useEffect(() => {
    if (trackCoordinates.length < 2) return;

    const bounds = L.latLngBounds(
      trackCoordinates.map(([lat, lng]) => L.latLng(lat, lng)),
    );
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [padding, padding] });
    }
  }, [dataKey, map, trackCoordinates, padding]);

  return null;
}

/**
 * BaseMap component for rendering multiple map layers
 * Matches the original Next.js GenericMap/BaseMap component
 *
 * @param {MapProps} props - Component properties
 * @param {[number, number]} props.center - Center coordinates [lat, lng], defaults to US center
 * @param {number} props.zoom - Initial zoom level, defaults to 4
 * @param {string} props.className - Additional CSS classes, defaults to 'rounded-md border shadow-inner'
 * @param {React.CSSProperties} props.style - Additional inline styles
 * @param {boolean} props.showZoomControls - Whether to show zoom controls, defaults to true
 */
export function BaseMap({
  center = [39.8283, -98.5795],
  zoom = 4,
  className = "rounded-md border shadow-inner",
  style = { zIndex: 0 },
  children,
  showZoomControls = true,
  doubleClickZoom = true,
  closePopupOnClick = true,
}: MapProps) {
  // Ensure the map has a low z-index to prevent it from overlapping headers and navbars
  const combinedStyle: React.CSSProperties = {
    height: "100%",
    width: "100%",
    ...style,
    zIndex: 0, // Explicitly setting to 0 to ensure it stays below other elements
    position: "relative" as const, // Make sure position is set for z-index to work properly
  };

  return (
    <div className={`map-container ${className}`} style={combinedStyle}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: "100%", width: "100%" }}
        zoomControl={showZoomControls}
        doubleClickZoom={doubleClickZoom}
        closePopupOnClick={closePopupOnClick}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a> | &copy; Itron Inc.'
          url={CARTO_LIGHT_ALL_URL}
        />
        <MapResizeInvalidator />
        {children}
      </MapContainer>
    </div>
  );
}

interface GeoJSONLayerProps {
  data: GeoJSONFeatureCollection;
  style?: PathOptions | ((feature: any) => PathOptions);
  onEachFeature?: (feature: any, layer: any) => void;
  fitBounds?: boolean;
  /**
   * Unique key to force re-render when data changes.
   * If not provided, a key is generated from feature IDs.
   */
  dataKey?: string;
  /**
   * Stable key for geographic data - controls when fitBounds runs.
   * Only re-fits bounds when this key changes.
   * If not provided, uses the dataKey or generated key.
   */
  fitBoundsKey?: string;
}

export function GeoJSONLayer({
  data,
  style,
  onEachFeature,
  fitBounds = true,
  dataKey,
  fitBoundsKey,
}: GeoJSONLayerProps) {
  const geoJSONRef = useRef<LeafletGeoJSON>(null);

  // Default style matching the original Next.js GeoJSONLayer
  const defaultStyle: PathOptions = {
    color: "#3388ff",
    weight: 2,
    opacity: 0.8,
    fillColor: "var(--neutral-50)", // #D6DEDF
    fillOpacity: 0.2,
  };

  // Generate a unique key from data if not provided
  // This forces React to unmount/remount the GeoJSON component when data changes
  const uniqueKey =
    dataKey || data.features.map((f) => f.id || f.properties?.id).join("-");

  // Use fitBoundsKey for controlling when to re-fit bounds (defaults to uniqueKey)
  const boundsKey = fitBoundsKey || uniqueKey;

  return (
    <>
      {fitBounds && <FitBounds geoJSON={data} dataKey={boundsKey} />}
      <GeoJSON
        key={uniqueKey}
        ref={geoJSONRef}
        data={data as any}
        style={style || defaultStyle}
        onEachFeature={onEachFeature}
      />
    </>
  );
}

export default BaseMap;
